import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { SearchService } from '@app/core/services/search/search.service';
import { Search } from '@app/shared/models/search/search.model';
import { routes } from '@app/consts';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

const SEARCH_DEBOUNCE_MS = 400;

@Component({
    selector: 'app-site-search-bar',
    templateUrl: './site-search-bar.component.html',
    styleUrls: ['./site-search-bar.component.scss']
})
export class SiteSearchBarComponent implements OnInit, OnDestroy {
    searchValue: string;
    searchRequest$: any;
    results: Search;
    skip: number = 0;
    take: number = 5;
    public isLoading;
    public routes: typeof routes = routes;
    loadingMore: boolean = false;
    activeResultId: string | null = null;

    private searchValueChanged$ = new Subject<string>();
    private searchValueChangedSubscription: Subscription;

    constructor(
        public translate: TranslateService,
        private searchService: SearchService,
        private router: Router,
        private el: ElementRef
    ) {
        // force route reload whenever params change;
    }

    ngOnInit(): void {
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;

        this.searchValueChangedSubscription = this.searchValueChanged$
            .pipe(
                debounceTime(SEARCH_DEBOUNCE_MS),
                distinctUntilChanged()
            )
            .subscribe(() => this.search());
    }

    ngOnDestroy(): void {
        this.searchValueChangedSubscription?.unsubscribe();
    }

    onSearchInput(): void {
        this.searchValueChanged$.next(this.searchValue);
    }

    searchRequest() {
        this.loadingMore === false ? this.isLoading = true : null;

        this.searchRequest$ = this.searchService.search(this.searchValue, this.take, this.skip)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                    this.loadingMore = false;
                })
            )
            .subscribe(
                res => {
                    this.results = res;
                }
            );
    }

    loadMore() {
        this.take = this.take + 5;
        this.loadingMore = true;
        this.search();
    }

    search() {
        if (this.searchRequest$) {
            this.searchRequest$.unsubscribe();
        }
        this.activeResultId = null;
        this.searchRequest();
    }

    clearSearch() {
        this.searchValue = '';
        this.activeResultId = null;
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.clearSearch();
            return;
        }
        if (event.key === 'ArrowDown' && this.results) {
            event.preventDefault();
            this.focusResultAt(0);
        }
    }

    onResultKeydown(event: KeyboardEvent, resultEl: HTMLElement): void {
        const results = this.getAllResultLinks();
        const idx = results.indexOf(resultEl);

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (idx < results.length - 1) {
                this.focusResultAt(idx + 1);
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (idx > 0) {
                this.focusResultAt(idx - 1);
            } else {
                this.activeResultId = null;
                (this.el.nativeElement.querySelector('#header-site-search-input') as HTMLElement)?.focus();
            }
        } else if (event.key === 'Escape') {
            this.clearSearch();
            (this.el.nativeElement.querySelector('#header-site-search-input') as HTMLElement)?.focus();
        }
    }

    private focusResultAt(idx: number): void {
        const results = this.getAllResultLinks();
        const target = results[idx];
        if (target) {
            target.focus();
            this.activeResultId = target.id || null;
        }
    }

    private getAllResultLinks(): HTMLElement[] {
        return Array.from(
            this.el.nativeElement.querySelectorAll('#header-site-search-results a.result') as NodeListOf<HTMLElement>
        );
    }

    get totalResultCount(): number {
        const employeeCount = this.results?.employees?.length || 0;
        const positionCount = this.results?.positions?.length || 0;

        return employeeCount + positionCount;
    }

    get hasNoResults(): boolean {
        return !!this.results && this.totalResultCount === 0;
    }

}
